import {
	MSC3575_STATE_KEY_LAZY,
	MSC3575_STATE_KEY_ME,
	MSC3575_WILDCARD,
	type MSC3575List,
	type MSC3575RoomSubscription
} from 'matrix-js-sdk/lib/sliding-sync';
import { RoomType, SlidingSyncListKey, StateEvent } from '$lib/enums';

export const SLIDING_SYNC_UNSTABLE_FEATURE = 'org.matrix.simplified_msc3575';
export const SLIDING_SYNC_TIMEOUT_MS = 20_000;
export const SLIDING_SYNC_INITIAL_RANGE_END = 19;
/** Ceiling for a cache-sized first window; anything beyond it is spidered as usual. */
export const SLIDING_SYNC_CACHED_RANGE_MAX = 200;
export const SLIDING_SYNC_SPIDER_STEP = 50;
export const SLIDING_SYNC_SPIDER_DELAY_MS = 50;
export const SLIDING_SYNC_OPEN_ROOM_TIMELINE_LIMIT = 50;

export const SLIDING_SYNC_LIST_KEYS: readonly SlidingSyncListKey[] =
	Object.values(SlidingSyncListKey);

/** State every nav/list surface reads without opening the room. */
export const NAV_REQUIRED_STATE: string[][] = [
	[StateEvent.ROOM_CREATE, ''],
	[StateEvent.ROOM_NAME, ''],
	[StateEvent.ROOM_AVATAR, ''],
	[StateEvent.ROOM_TOPIC, ''],
	[StateEvent.ROOM_CANONICAL_ALIAS, ''],
	[StateEvent.ROOM_JOIN_RULES, ''],
	[StateEvent.ROOM_TOMBSTONE, ''],
	[StateEvent.ROOM_ENCRYPTION, ''],
	[StateEvent.ROOM_POWER_LEVELS, ''],
	[StateEvent.SPACE_CHILD, MSC3575_WILDCARD],
	[StateEvent.SPACE_PARENT, MSC3575_WILDCARD],
	[StateEvent.PONIES_ROOM_EMOTES, MSC3575_WILDCARD],
	[StateEvent.ROOM_MEMBER, MSC3575_STATE_KEY_ME]
];

const initialRange = (): number[][] => [[0, SLIDING_SYNC_INITIAL_RANGE_END]];

export const SLIDING_SYNC_LISTS: Record<SlidingSyncListKey, MSC3575List> = {
	spaces: {
		ranges: initialRange(),
		filters: { room_types: [RoomType.SPACE], is_invite: false },
		required_state: NAV_REQUIRED_STATE,
		timeline_limit: 0
	},
	invites: {
		ranges: initialRange(),
		filters: { is_invite: true },
		required_state: NAV_REQUIRED_STATE,
		timeline_limit: 0
	},
	rooms: {
		ranges: initialRange(),
		filters: { not_room_types: [RoomType.SPACE], is_invite: false },
		// the last sender is needed for DM avatar fallbacks; timeline_limit 1 feeds activity sort
		required_state: [...NAV_REQUIRED_STATE, [StateEvent.ROOM_MEMBER, MSC3575_STATE_KEY_LAZY]],
		timeline_limit: 1
	}
};

const OLD_ROOMS_SUBSCRIPTION: MSC3575RoomSubscription = {
	required_state: [
		[StateEvent.ROOM_CREATE, ''],
		[StateEvent.ROOM_TOMBSTONE, ''],
		[StateEvent.ROOM_ENCRYPTION, ''],
		[StateEvent.ROOM_MEMBER, MSC3575_STATE_KEY_ME]
	],
	timeline_limit: 0
};

/** Default subscription: full state so rust-crypto knows every member to encrypt for. */
export const ROOM_SUBSCRIPTION_ENCRYPTED: MSC3575RoomSubscription = {
	required_state: [[MSC3575_WILDCARD, MSC3575_WILDCARD]],
	timeline_limit: SLIDING_SYNC_OPEN_ROOM_TIMELINE_LIMIT,
	include_old_rooms: OLD_ROOMS_SUBSCRIPTION
};

export const ROOM_SUBSCRIPTION_UNENCRYPTED_NAME = 'unencrypted';
export const ROOM_SUBSCRIPTION_UNENCRYPTED: MSC3575RoomSubscription = {
	required_state: [
		...NAV_REQUIRED_STATE,
		[StateEvent.ROOM_PINNED_EVENTS, ''],
		[StateEvent.ROOM_HISTORY_VISIBILITY, ''],
		[StateEvent.ROOM_GUEST_ACCESS, ''],
		[StateEvent.ROOM_SERVER_ACL, ''],
		[StateEvent.POWER_LEVEL_TAGS, ''],
		[StateEvent.GROUP_CALL_PREFIX, MSC3575_WILDCARD],
		[StateEvent.GROUP_CALL_MEMBER_PREFIX, MSC3575_WILDCARD],
		[StateEvent.ROOM_MEMBER, MSC3575_STATE_KEY_LAZY]
	],
	timeline_limit: SLIDING_SYNC_OPEN_ROOM_TIMELINE_LIMIT,
	include_old_rooms: OLD_ROOMS_SUBSCRIPTION
};
